<template>
  <q-dialog
    v-model="shown"
    persistent
  >
    <q-card
      data-test-id="select-register-station-card"
    >
      <q-inner-loading
        style="z-index: 1;"
        :showing="loading"
      />
      <q-card-section
        v-if="currentUser"
        class="column items-center"
      >
        <q-select
          v-model="currentUser.circuitId"
          data-test-id="circuit-select"
          class="set-register-station-dialog-select q-mr-md q-my-sm full-width"
          label="Circuit"
          :options="currentUser.authorizedCircuitOptions"
          option-label="name"
          option-value="id"
          map-options
          emit-value
          :disable="loading"
          @update:model-value="handleCircuitChange()"
        />
        <q-select
          v-if="!userCircuitMigratedUrl"
          v-model="siteId"
          data-test-id="site-select"
          class="set-register-station-dialog-select q-mr-md q-my-sm full-width"
          label="Site"
          :options="currentUser.authorizedSiteOptions"
          option-label="name"
          option-value="id"
          map-options
          emit-value
          :disable="loading"
          :readonly="!currentUser.authorizedSiteOptions.length"
          @update:model-value="handleSiteChange()"
        >
          <q-tooltip v-if="!currentUser.authorizedSiteOptions.length">
            No sites available for this circuit.
          </q-tooltip>
        </q-select>
        <q-select
          v-if="!userCircuitMigratedUrl"
          v-model="registerStationId"
          data-test-id="register-station-select"
          class="set-register-station-dialog-select q-mr-md q-my-sm full-width"
          label="Register Station"
          :options="registerStationOptions"
          option-label="name"
          option-value="id"
          map-options
          emit-value
          :disable="loading"
          :readonly="!registerStationOptions.length"
          @update:model-value="setRegisterStation()"
        >
          <q-tooltip v-if="!registerStationOptions.length">
            No register stations available for this site.
          </q-tooltip>
        </q-select>
        <div
          v-else
          class="col-auto text-h6"
        >
          This site has been migrated to <a :href="userCircuitMigratedUrl">our new Beta region</a>.
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-if="!userCircuitMigratedUrl"
          data-test-id="quit-register-button"
          label="Quit"
          color="primary"
          flat
          @click="quit"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { appMode, isPos } from 'src/config'
import RegisterClient from 'src/models/register/RegisterClient'

const effectiveAppMode = isPos ? 'register' : appMode

export default {
  name: 'SetRegisterStationDialog',
  context: ['currentUser', 'currentRegisterClient'],
  configuration: ['registerStations'],
  props: {
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'site-changed'],
  data () {
    return {
      shown: false,
      registerStationId: null,
      siteId: null,
      loading: false
    }
  },
  computed: {
    userCircuitMigratedUrl () {
      return this.currentUser?.circuitMigratedUrl
    },
    registerStationOptions () {
      if (!this.registerStations?.data) return []

      return _.filter(this.registerStations.data, registerStation =>
        registerStation?.isUnarchived &&
        registerStation?.registerType?.applicationType === effectiveAppMode
      )
    }
  },
  watch: {
    modelValue: {
      handler () {
        this.shown = this.modelValue
      },
      immediate: true
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    }
  },
  mounted () {
    this.shown = this.modelValue
    // Initialize circuit ID to first authorized circuit if current one is invalid
    if (this.currentUser?.circuitId && this.currentUser?.authorizedCircuitOptions?.length) {
      const isValidCircuit = this.currentUser.authorizedCircuitOptions.some(
        circuit => circuit.id === this.currentUser.circuitId
      )
      if (!isValidCircuit) {
        this.currentUser.circuitId = this.currentUser.authorizedCircuitOptions[0].id
      }
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    hide () {
      this.shown = false
    },
    quit () {
      this.$quitRegister()
    },
    async setRegisterStation () {
      try {
        this.loading = true
        let registerClient
        if (this.currentRegisterClient) {
          registerClient = this.currentRegisterClient
          registerClient.registerStationId = this.registerStationId
        } else {
          registerClient = new RegisterClient({ registerStationId: this.registerStationId })
        }
        await registerClient.save()
      } catch (exception) {
        this.$rollbar.error(`Failed to setRegisterStation(), exception: ${exception.message}`)
      } finally {
        this.loading = false
        window.location.reload()
      }
    },
    async handleSiteChange () {
      try {
        this.loading = true
        this.currentUser.siteId = this.siteId
        await this.$changeCircuitOrSite()
        await this.$handleAuthChange()
        this.$emit('site-changed', this.siteId)
      } catch (exception) {
        this.$rollbar.error(`Failed to handleSiteChange(), exception: ${exception.message}`)
      } finally {
        this.loading = false
      }
    },
    async handleCircuitChange () {
      try {
        this.loading = true
        await this.$changeCircuitOrSite()
        await this.$handleAuthChange()
      } catch (exception) {
        this.$rollbar.error(`Failed to handleCircuitChange(), exception: ${exception.message}`)
      } finally {
        this.loading = false
      }
    }
  }

}
</script>

<style scoped>
.set-register-station-dialog-select {
  min-width: 200px;
}
</style>
